/**
 * ============================================================================
 * ⭐ "POSSIBLE NO-DEPARTURE (N)" — the board chip and its list (F106, Decision 283)
 * ============================================================================
 *
 * > Pete, 2026-09-29: *"I think we also need a bubble for Possible No Departed next
 * > to 29 appointments too"* → *"Arrived on a past day"* · *"Departed at…"* ·
 * > *"Possible no-departure (N)"*.
 *
 * A truck marked Arrived on a day before today with no departure recorded. It has
 * almost certainly gone; nobody pressed Depart. Its dwell and detention clocks never
 * stop until someone says when it left.
 *
 * ⭐ THE DEFAULT TIME: the last finished unload / load, else the booked end — never
 * earlier than the arrival. The server re-checks (not in the future, not before the
 * arrival) and records it as a late entry that skipped the outbound release check.
 *
 * ⚠️ HIDDEN AT ZERO, like Possible no-shows. The list is the server's (every past day
 * at this facility, bounded to its company, §0.0).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LogOut } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { DirectionTag } from '@/components/DirectionTag';
import { OpenItemChip } from '@/components/OpenItemChip';
import type { PossibleNoDepartureListDto, PossibleNoDepartureRowDto } from '@/api/types';
import { listPossibleNoDepartures, markPossibleNoDepartureDeparted } from '@/api/client';

const REFRESH_MS = 5 * 60 * 1000;

function toLocalInput(iso: string): string {
    const d = new Date(iso);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

const fmt = (iso: string | null) => (iso
    ? new Date(iso).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '—');

/** ⭐ The best guess at when it left — see the header. Exported for the guard. */
export function defaultDepartureIso(r: Pick<PossibleNoDepartureRowDto, 'lastLabourFinishUtc' | 'scheduledEndUtc' | 'actualArrivalUtc'>): string {
    const cand = r.lastLabourFinishUtc ?? r.scheduledEndUtc;
    const arrival = r.actualArrivalUtc ? new Date(r.actualArrivalUtc).getTime() : null;
    const c = new Date(cand).getTime();
    if (arrival !== null && (!Number.isFinite(c) || c < arrival)) return new Date(arrival).toISOString();
    return new Date(c).toISOString();
}

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function PossibleNoDeparturesChip({ facilityCode, getToken, onChanged }: {
    facilityCode: string | null;
    getToken: () => Promise<string>;
    onChanged?: () => void;
}) {
    const [data, setData] = useState<PossibleNoDepartureListDto | null>(null);
    const [open, setOpen] = useState(false);
    const [loadErr, setLoadErr] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!facilityCode) { setData(null); return; }
        try {
            setData(await listPossibleNoDepartures(await getToken(), facilityCode));
            setLoadErr(null);
        } catch (e) {
            setLoadErr(errText(e));
        }
    }, [facilityCode, getToken]);

    useEffect(() => {
        void load();
        const t = window.setInterval(() => { void load(); }, REFRESH_MS);
        const onFocus = () => { void load(); };
        window.addEventListener('focus', onFocus);
        return () => { window.clearInterval(t); window.removeEventListener('focus', onFocus); };
    }, [load]);

    const total = data?.total ?? 0;
    if (!facilityCode || (total === 0 && !open)) return null;

    const rows = data?.rows ?? [];
    const hidden = total - rows.length;
    return (
        <>
            <OpenItemChip
                onClick={() => { setOpen(true); void load(); }}
                title="Trucks marked Arrived on a past day with no departure recorded — say when each one left"
                icon={<LogOut className="h-3 w-3" aria-hidden="true" />}
            >
                Possible no-departure ({total})
            </OpenItemChip>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-3xl">
                    <DialogHeader>
                        <DialogTitle>Possible no-departure ({total})</DialogTitle>
                    </DialogHeader>
                    <p className="text-xs text-muted-foreground">
                        These trucks were marked Arrived on an earlier day and never marked Departed. Enter the time
                        each one left — it is recorded as a late entry.
                    </p>
                    {loadErr && <div className="text-xs text-rose-700">The list could not be read: {loadErr}</div>}
                    {rows.length === 0 && !loadErr && (
                        <div className="py-6 text-center text-sm text-muted-foreground">Nothing left to mark.</div>
                    )}
                    {rows.length > 0 && (
                        <div className="max-h-[60vh] divide-y overflow-y-auto rounded border">
                            {rows.map((r) => (
                                <NoDepartureRow key={r.appointmentId} row={r} facilityCode={facilityCode}
                                    getToken={getToken}
                                    onMarked={(id) => {
                                        setData((d) => d ? { total: Math.max(0, d.total - 1), rows: d.rows.filter(x => x.appointmentId !== id) } : d);
                                        onChanged?.();
                                    }} />
                            ))}
                        </div>
                    )}
                    {hidden > 0 && (
                        <div className="text-[11px] text-muted-foreground">
                            Showing the {rows.length} most recent; {hidden} older ones appear as these are marked.
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}

function NoDepartureRow({ row, facilityCode, getToken, onMarked }: {
    row: PossibleNoDepartureRowDto;
    facilityCode: string;
    getToken: () => Promise<string>;
    onMarked: (appointmentId: number) => void;
}) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const initial = useMemo(() => toLocalInput(defaultDepartureIso(row)), [row]);
    const [local, setLocal] = useState(initial);
    const direction = row.direction === 'OUTBOUND' ? 'OUTBOUND' : 'INBOUND';
    const ref = [row.poNumber && `PO ${row.poNumber}`, row.referenceNumber && `Ref ${row.referenceNumber}`].filter(Boolean).join(' · ');

    const save = async () => {
        setBusy(true); setErr(null);
        try {
            await markPossibleNoDepartureDeparted(await getToken(), facilityCode, row.appointmentId, new Date(local).toISOString());
            onMarked(row.appointmentId);
        } catch (e) {
            setErr(errText(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex flex-col gap-1 px-3 py-2 text-xs">
            <div className="flex items-center gap-2">
                <DirectionTag direction={direction} />
                <span className="font-medium">{row.appointmentNumber}</span>
                <span className="min-w-0 truncate">
                    {[row.clientName || row.clientCode, row.carrierName || row.carrierCode].filter(Boolean).join(' · ')}
                </span>
                <span className="ml-auto flex shrink-0 items-center gap-1">
                    <span className="text-muted-foreground">Departed at</span>
                    <input type="datetime-local" className="rounded border px-1 py-0.5 text-xs"
                           value={local} onChange={(e) => setLocal(e.target.value)} />
                    <Button size="sm" disabled={busy || !local} onClick={() => void save()}>Departed</Button>
                </span>
            </div>
            <div className="text-[11px] text-muted-foreground">
                {[`Arrived ${fmt(row.actualArrivalUtc)}`,
                  row.lastLabourFinishUtc && `labor finished ${fmt(row.lastLabourFinishUtc)}`,
                  row.doorCode && `Door ${row.doorCode}`, ref].filter(Boolean).join(' · ')}
            </div>
            {err && <div className="text-[11px] text-rose-700">{err}</div>}
        </div>
    );
}
